import React, { useEffect, useMemo, useState } from "react";
import { ArrowLeft } from "lucide-react";
import i18n from "../../i18n";

const PVACase = ({ imageName = "pension.jpg" }) => {
  const [langTick, setLangTick] = useState(0);

  useEffect(() => {
    const onLang = () => setLangTick((x) => x + 1);
    i18n.on("languageChanged", onLang);
    return () => i18n.off("languageChanged", onLang);
  }, []);

  const tr = useMemo(() => {
    void langTick;

    return {
      back: i18n.t("pvaCase.back"),

      durationLabel: i18n.t("pvaCase.labels.duration"),
      toolsLabel: i18n.t("pvaCase.labels.tools"),

      org: {
        title: i18n.t("pvaCase.org.title"),
        description: i18n.t("pvaCase.org.description"),
        duration: i18n.t("pvaCase.org.duration"),
      },

      intro: {
        title: i18n.t("pvaCase.intro.title"),
        text: i18n.t("pvaCase.intro.text"),
      },

      upgrade: {
        duration: i18n.t("pvaCase.upgrade.duration"),
        title: i18n.t("pvaCase.upgrade.title"),
        text: i18n.t("pvaCase.upgrade.text"),
        bullets: [
          i18n.t("pvaCase.upgrade.bullets.0"),
          i18n.t("pvaCase.upgrade.bullets.1"),
          i18n.t("pvaCase.upgrade.bullets.2"),
          i18n.t("pvaCase.upgrade.bullets.3"),
        ],
      },

      migration: {
        duration: i18n.t("pvaCase.migration.duration"),
        title: i18n.t("pvaCase.migration.title"),
        text: i18n.t("pvaCase.migration.text"),
        bullets: [
          i18n.t("pvaCase.migration.bullets.0"),
          i18n.t("pvaCase.migration.bullets.1"),
          i18n.t("pvaCase.migration.bullets.2"),
          i18n.t("pvaCase.migration.bullets.3"),
        ],
      },

      optimize: {
        duration: i18n.t("pvaCase.optimize.duration"),
        title: i18n.t("pvaCase.optimize.title"),
        text: i18n.t("pvaCase.optimize.text"),
        bullets: [
          i18n.t("pvaCase.optimize.bullets.0"),
          i18n.t("pvaCase.optimize.bullets.1"),
          i18n.t("pvaCase.optimize.bullets.2"),
          i18n.t("pvaCase.optimize.bullets.3"),
        ],
      },

      legacy: {
        duration: i18n.t("pvaCase.legacy.duration"),
        title: i18n.t("pvaCase.legacy.title"),
        text: i18n.t("pvaCase.legacy.text"),
        bullets: [
          i18n.t("pvaCase.legacy.bullets.0"),
          i18n.t("pvaCase.legacy.bullets.1"),
          i18n.t("pvaCase.legacy.bullets.2"),
          i18n.t("pvaCase.legacy.bullets.3"),
        ],
      },
    };
  }, [langTick]);

  const tools = ["Java", "Microfocus", "Oracle", "Linux", "IBM"];

  const toolMap = {
    Microfocus: "microfocus.png",
    Oracle: "oracle.png",
    Linux: "linux.png",
    IBM: "ibm.png",
  };

  const handleBack = () => window.history.back();
  const imageUrl = `${process.env.PUBLIC_URL}/images/${imageName}`;

  return (
    <div>
      <div
        className="relative h-64 w-full bg-cover bg-top flex items-start justify-start"
        style={{ backgroundImage: `url(${imageUrl})` }}
      >
        <div className="max-w-screen-xl w-full">
          <button
            onClick={handleBack}
            className="flex items-center gap-2 text-gray-700 text-base font-medium py-2 px-4 bg-white/70 hover:bg-white rounded-full m-8"
            type="button"
          >
            <ArrowLeft className="w-5 h-5" />
            {tr.back}
          </button>
        </div>
      </div>

      <div className="flex flex-col md:flex-row gap-8 p-4 md:p-8">
        <div className="w-full md:w-[320px]">
          <div className="p-4 flex flex-col gap-4">
            <img
              src={`${process.env.PUBLIC_URL}/logos/pv.png`}
              alt="Pension Logo"
              className="h-14 object-contain self-start"
            />

            <div>
              <h2 className="text-lg font-semibold">{tr.org.title}</h2>
              <p className="text-sm text-gray-600">{tr.org.description}</p>
            </div>

            <div>
              <div className="text-sm text-gray-400">{tr.durationLabel}</div>
              <div className="text-sm font-medium">{tr.org.duration}</div>
            </div>

            <div className="mt-6">
              <div className="text-sm text-gray-400 mb-2">{tr.toolsLabel}</div>
              <div className="flex flex-col gap-2">
                {tools.map((tool) => {
                  const iconFile = `${process.env.PUBLIC_URL}/tools/${
                    toolMap[tool] || `${tool.toLowerCase()}.png`
                  }`;

                  return (
                    <div
                      key={tool}
                      className="flex items-center gap-3 px-5 py-2 bg-white rounded-full shadow text-sm font-medium w-[160px]"
                    >
                      <img
                        src={iconFile}
                        alt={`${tool} icon`}
                        className="w-7 h-7 object-contain"
                      />
                      {tool}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>

        <div className="flex-1">
          <div className="px-2 sm:px-4 md:px-0">
            <div className="bg-white text-black font-sans px-0 md:px-6 py-8 space-y-10 rounded-xl">
              <div className="px-4 md:px-0 space-y-10">
                <section className="space-y-4">
                  <h2 className="text-xl font-semibold text-gray-800">
                    {tr.intro.title}
                  </h2>
                  <p>{tr.intro.text}</p>
                </section>
              </div>

              {[tr.upgrade, tr.migration, tr.optimize, tr.legacy].map(
                (block, idx) => (
                  <section
                    key={idx}
                    className="bg-gray-100 p-6 rounded-xl space-y-4"
                  >
                    <p className="text-sm text-gray-500 font-medium">
                      {tr.durationLabel} {block.duration}
                    </p>
                    <h3 className="text-lg font-semibold">{block.title}</h3>
                    <p>{block.text}</p>
                    <ul className="list-disc list-inside text-gray-800">
                      {block.bullets.map((b, i) => (
                        <li key={i}>{b}</li>
                      ))}
                    </ul>
                  </section>
                )
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PVACase;



